/**
 * 이상한 방 - 밴드 격자 평면도 빌더
 *
 * `floorPlans.ts`의 규모별 명세(`BandPlanSpec`)를 받아 좌표가 있는 `FloorPlan`으로 굽는다.
 * 데이터 모델(`floorPlanModel.ts`)만 알고, 어떤 평면도가 있는지는 모른다 -
 * 명세 표는 이 파일을 import 하는 쪽(`floorPlans.ts`)이 들고 있어야 순환이 생기지 않는다.
 * 호출부는 이 파일을 직접 보지 않는다(배럴 `floorPlan.ts`는 빌더를 내보내지 않는다).
 *
 * ## 왜 좌표를 손으로 적지 않는가
 *
 * `DEFAULT_FLOOR_PLAN`은 손으로 적은 9구획이고, 그 안에서 좌표 하나를 고치면
 * 인접한 방 rect·문 셀·창 위치가 전부 따라 움직인다. 규모가 다섯 벌이 되는 순간
 * 이 방식은 **네 벌의 어긋남**을 뜻한다(첫 벌만 손으로 맞춰 보기 때문이다).
 *
 * 그래서 나머지 평면도들은 **밴드 격자**로 적는다. x·z 각각을 몇 칸짜리 띠로 자를지만 적으면
 * 밴드 사이 한 칸이 자동으로 내벽이 되고, 구획은 "몇 번째 띠에 앉는가"로만 지정된다.
 * 좌표가 데이터에 없으므로 좌표가 어긋날 수 없다.
 *
 * 문도 좌표가 아니라 **어느 두 구획을 잇는가**로 적는다. 맞닿은 벽과 그 위의 위치는
 * 두 rect에서 유도된다 - "문이 벽 위에 없다"는 종류의 오류가 데이터에서 사라진다.
 */

import {
  OUTER_WALL_CELLS,
  WALL_THICKNESS_CELLS,
  type Axis,
  type Cell2,
  type CellRect,
  type Doorway,
  type FloorPlan,
  type Room,
  type RoomId,
  type RoomSide,
  type RoomTag,
  type WindowOpening,
} from './floorPlanModel'
import type { RoomBounds } from './types'

/* --- 공용 규격 ------------------------------------------------------------------ */

/**
 * 천장 높이(셀). 12 × 0.25m = 3.0m.
 *
 * 창 규격과 함께 여기 두는 이유: 빌더가 창을 유도할 때 읽고, 손으로 적은 아파트(`floorPlans.ts`)도
 * 같은 값을 써야 규모가 바뀌어도 천장·창 치수가 어긋나지 않는다. 빌더가 명세 표보다 아래층이므로
 * 둘이 공유하는 규격은 빌더 쪽에 산다.
 */
export const PLAN_HEIGHT_CELLS = 12

/** 창 규격. 창턱 1.0m, 유리 1.5m → 상단 2.5m. 욕실만 시선 차단을 위해 높이 단다. */
export const WINDOW_SILL_CELLS = 4
export const WINDOW_HEIGHT_CELLS = 6
export const BATH_WINDOW_SILL_CELLS = 6
export const BATH_WINDOW_HEIGHT_CELLS = 4

/* --- 밴드 격자 ------------------------------------------------------------------ */

/** 밴드 격자 한 칸에 앉는 구획. `colSpan`/`rowSpan`은 밴드 여러 개를 사이의 내벽까지 삼킨다. */
export type ZoneSpec = Readonly<{
  id: RoomId
  name: string
  tag: RoomTag
  col: number
  row: number
  colSpan?: number
  rowSpan?: number
}>

/** 문 하나. 좌표가 없다 - 두 구획이 맞닿은 벽의 한가운데로 자동 배치된다. */
export type DoorSpec = Readonly<{ from: RoomId; to: RoomId; width: number }>

export type BandPlanSpec = Readonly<{
  id: string
  /** x축 띠의 내부 폭(셀). 띠 사이마다 내벽 한 칸이 자동으로 들어간다. */
  xBands: readonly number[]
  zBands: readonly number[]
  zones: readonly ZoneSpec[]
  doors: readonly DoorSpec[]
}>

/** 밴드 목록이 만들어 내는 축 전체 길이(외벽 두 겹 + 띠들 + 띠 사이 내벽). */
function bandAxisSize(bands: readonly number[]): number {
  const inner = bands.reduce((sum, size) => sum + size, 0)
  const innerWalls = Math.max(bands.length - 1, 0) * WALL_THICKNESS_CELLS
  return OUTER_WALL_CELLS * 2 + inner + innerWalls
}

/** 각 띠의 시작 좌표. 외벽 안쪽에서 출발해 띠 하나마다 내벽 한 칸을 건너뛴다. */
function bandStarts(bands: readonly number[]): readonly number[] {
  const starts: number[] = []
  let cursor = OUTER_WALL_CELLS

  for (const size of bands) {
    starts.push(cursor)
    cursor += size + WALL_THICKNESS_CELLS
  }

  return starts
}

/** 범위를 벗어난 밴드 인덱스는 데이터 오류다. 조용히 0으로 떨어지면 구획이 겹쳐 나온다. */
function bandValueAt(values: readonly number[], index: number, label: string): number {
  const value = values[index]
  if (value === undefined) {
    throw new RangeError(`밴드 인덱스 ${index}가 ${label} 범위를 벗어난다(0~${values.length - 1})`)
  }
  return value
}

/** `index`부터 `span`개 띠를 삼킨 크기. 삼킨 띠 사이의 내벽도 구획 내부가 된다. */
function bandSpanSize(bands: readonly number[], index: number, span: number, label: string): number {
  if (!Number.isInteger(span) || span < 1) {
    throw new RangeError(`${label} span은 1 이상의 정수여야 한다: ${span}`)
  }

  let total = 0
  for (let step = 0; step < span; step += 1) {
    total += bandValueAt(bands, index + step, label)
  }
  return total + (span - 1) * WALL_THICKNESS_CELLS
}

/* --- 문 좌표 유도 --------------------------------------------------------------- */

/** 두 구획이 맞닿은 벽. `line`은 벽이 놓인 열(axis 'z') 또는 행(axis 'x')이다. */
type SharedWall = Readonly<{ axis: Axis; line: number; spanStart: number; spanSize: number }>

function overlapSpan(aStart: number, aSize: number, bStart: number, bSize: number): { start: number; size: number } {
  const start = Math.max(aStart, bStart)
  const end = Math.min(aStart + aSize, bStart + bSize)
  return { start, size: end - start }
}

/**
 * 두 rect 사이의 맞닿은 벽. 벽이 1셀이므로 "한 칸 띄우고 이웃한가"만 보면 된다.
 * 맞닿지 않았으면 `null` - 호출부가 데이터 오류로 처리한다.
 */
function sharedWall(a: CellRect, b: CellRect): SharedWall | null {
  const gap = WALL_THICKNESS_CELLS

  if (a.x + a.sizeX + gap === b.x || b.x + b.sizeX + gap === a.x) {
    const line = a.x < b.x ? a.x + a.sizeX : b.x + b.sizeX
    const span = overlapSpan(a.z, a.sizeZ, b.z, b.sizeZ)
    return span.size > 0 ? { axis: 'z', line, spanStart: span.start, spanSize: span.size } : null
  }

  if (a.z + a.sizeZ + gap === b.z || b.z + b.sizeZ + gap === a.z) {
    const line = a.z < b.z ? a.z + a.sizeZ : b.z + b.sizeZ
    const span = overlapSpan(a.x, a.sizeX, b.x, b.sizeX)
    return span.size > 0 ? { axis: 'x', line, spanStart: span.start, spanSize: span.size } : null
  }

  return null
}

/** 문 하나를 두 구획에서 유도한다. 맞닿은 구간의 **한가운데**에 놓는다. */
function resolveDoorway(from: Room, to: Room, width: number): Doorway {
  const wall = sharedWall(from.rect, to.rect)
  if (wall === null) {
    throw new RangeError(`구획 '${from.id}'와 '${to.id}'는 맞닿아 있지 않아 문을 낼 수 없다`)
  }
  if (width > wall.spanSize) {
    throw new RangeError(
      `문 '${from.id}'↔'${to.id}'의 폭 ${width}가 맞닿은 구간 ${wall.spanSize}칸을 넘는다`,
    )
  }

  const start = wall.spanStart + Math.floor((wall.spanSize - width) / 2)
  const cell: Cell2 = wall.axis === 'x' ? { x: start, z: wall.line } : { x: wall.line, z: start }

  return { fromRoomId: from.id, toRoomId: to.id, cell, width, axis: wall.axis }
}

/* --- 창 유도 -------------------------------------------------------------------- */

/** 창 하나가 벽 양 끝에서 비워 두는 여백(셀). 모서리에 딱 붙은 창은 벽 기둥을 지운다. */
const WINDOW_EDGE_MARGIN_CELLS = 2
/** 이보다 좁으면 창을 내지 않는다 - 0.5m짜리 창은 채광도 실루엣도 만들지 못한다. */
const WINDOW_MIN_WIDTH_CELLS = 4
/** 창 폭 상한(3.5m). 벽면이 통째로 유리가 되면 벽 가구의 배경이 사라진다. */
const WINDOW_MAX_WIDTH_CELLS = 14

/**
 * 한 구획이 가질 수 있는 창의 수.
 *
 * 외벽에 닿는 면마다 창을 내면 모서리 방이 사방으로 유리가 되어 온실처럼 보이고,
 * 무엇보다 **벽에 등을 붙이는 가구의 배경**이 남지 않는다(`furnitureGroups`의 `againstWall`).
 * 둘로 제한하면 어느 방이든 최소 두 면은 온전한 벽으로 남는다.
 */
const WINDOWS_PER_ROOM_MAX = 2

/** 창을 낼 면의 우선순위. 남향을 먼저 잡는 것은 조명 리그의 키라이트 방향과 같은 이유다. */
const WINDOW_SIDE_PRIORITY: readonly RoomSide[] = ['south', 'west', 'east', 'north']

/** 이 구획이 외벽에 닿는 면 전부(우선순위 순). */
function exteriorSides(room: Room, bounds: RoomBounds): readonly RoomSide[] {
  const rect = room.rect
  const touches: Readonly<Record<RoomSide, boolean>> = {
    south: rect.z === OUTER_WALL_CELLS,
    west: rect.x === OUTER_WALL_CELLS,
    east: rect.x + rect.sizeX === bounds.sizeX - OUTER_WALL_CELLS,
    north: rect.z + rect.sizeZ === bounds.sizeZ - OUTER_WALL_CELLS,
  }

  return WINDOW_SIDE_PRIORITY.filter((side) => touches[side])
}

/** 그 면의 외벽 위에 놓이는 창 하나. 폭이 모자라면 `null`(창을 내지 않는다). */
function windowOnSide(room: Room, bounds: RoomBounds, side: RoomSide): WindowOpening | null {
  const rect = room.rect
  const alongX = side === 'south' || side === 'north'
  const spanStart = alongX ? rect.x : rect.z
  const spanSize = alongX ? rect.sizeX : rect.sizeZ

  const width = Math.min(WINDOW_MAX_WIDTH_CELLS, spanSize - WINDOW_EDGE_MARGIN_CELLS * 2)
  if (width < WINDOW_MIN_WIDTH_CELLS) return null

  const start = spanStart + Math.floor((spanSize - width) / 2)
  const lastX = bounds.sizeX - OUTER_WALL_CELLS
  const lastZ = bounds.sizeZ - OUTER_WALL_CELLS

  const cell: Cell2 =
    side === 'south'
      ? { x: start, z: 0 }
      : side === 'north'
        ? { x: start, z: lastZ }
        : side === 'west'
          ? { x: 0, z: start }
          : { x: lastX, z: start }

  // 욕실만 창턱을 높이고 유리를 낮춘다. 시선 차단이 곧 그 방의 성격이다.
  const isBath = room.tag === 'bath'

  return {
    id: `win-${room.id}-${side}`,
    roomId: room.id,
    cell,
    width,
    axis: alongX ? 'x' : 'z',
    sillCell: isBath ? BATH_WINDOW_SILL_CELLS : WINDOW_SILL_CELLS,
    heightCells: isBath ? BATH_WINDOW_HEIGHT_CELLS : WINDOW_HEIGHT_CELLS,
  }
}

function buildWindows(rooms: readonly Room[], bounds: RoomBounds): readonly WindowOpening[] {
  const windows: WindowOpening[] = []

  for (const room of rooms) {
    let placed = 0
    for (const side of exteriorSides(room, bounds)) {
      if (placed >= WINDOWS_PER_ROOM_MAX) break
      const opening = windowOnSide(room, bounds, side)
      if (opening === null) continue
      windows.push(opening)
      placed += 1
    }
  }

  return windows
}

/* --- 조립 ----------------------------------------------------------------------- */

/**
 * 밴드 명세 하나를 평면도로 굽는다. 검증은 하지 않는다 -
 * 캐시에 넣기 직전에 `getFloorPlan`(`floorPlans.ts`)이 한 번만 검증한다.
 */
export function buildBandPlan(spec: BandPlanSpec): FloorPlan {
  const bounds: RoomBounds = {
    sizeX: bandAxisSize(spec.xBands),
    sizeY: PLAN_HEIGHT_CELLS,
    sizeZ: bandAxisSize(spec.zBands),
  }

  const xStarts = bandStarts(spec.xBands)
  const zStarts = bandStarts(spec.zBands)

  const rooms: readonly Room[] = spec.zones.map((zone) => ({
    id: zone.id,
    name: zone.name,
    tag: zone.tag,
    rect: {
      x: bandValueAt(xStarts, zone.col, 'xBands'),
      z: bandValueAt(zStarts, zone.row, 'zBands'),
      sizeX: bandSpanSize(spec.xBands, zone.col, zone.colSpan ?? 1, 'xBands'),
      sizeZ: bandSpanSize(spec.zBands, zone.row, zone.rowSpan ?? 1, 'zBands'),
    },
  }))

  const byId = new Map(rooms.map((room) => [room.id, room]))
  const doorways: readonly Doorway[] = spec.doors.map((door) => {
    const from = byId.get(door.from)
    const to = byId.get(door.to)
    if (from === undefined || to === undefined) {
      throw new RangeError(`평면도 '${spec.id}'의 문이 없는 구획을 가리킨다: '${door.from}'↔'${door.to}'`)
    }
    return resolveDoorway(from, to, door.width)
  })

  return { id: spec.id, bounds, rooms, doorways, windows: buildWindows(rooms, bounds) }
}
